import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight, Check } from "lucide-react";
import useSeo from "../lib/useSeo";
import { trackPageView, trackStripeClick } from "../lib/track";

const STRIPE_LINK = "https://buy.stripe.com/3cI3cw0AA0ZW2u43YVcbC00";
const BUNDLE_STRIPE_LINK = "https://buy.stripe.com/6oU14o6YY4c80lWfHDcbC01";
const GOLD = "#C9A84C";
const GOLD_HOVER = "#b8973d";

const SECTIONS = [
  { num: "01", title: "Clarity Check", line: "Where are you actually stuck?" },
  { num: "02", title: "The One Thing Test", line: "What matters most right now?" },
  { num: "03", title: "Positioning Starter", line: "Who you are and who you serve." },
  { num: "04", title: "Offer Outline", line: "What are you actually selling?" },
  { num: "05", title: "30-Day Clarity Plan", line: "Your first move, not a full roadmap." },
  { num: "06", title: "Self-Review Checklist", line: "Are you ready to build?" },
];

const BONUSES = [
  { title: "Clarity Summary", body: "Your six exercises stitched into one shareable output." },
  { title: "7 Editable Templates", body: "The Kit's outputs as Google Docs and Word templates." },
  { title: "AI Without the Noise", body: "Four prompts to keep using AI well, without outsourcing judgment." },
];

const STEPS = [
  {
    n: "01",
    title: "Work through six exercises",
    body: "Go through six guided exercises at your own pace. No timers, no gates. Just six clear questions that move you forward.",
  },
  {
    n: "02",
    title: "Read the AI reflection",
    body: "Get an AI reflection after each section based on what you actually wrote. Pattern-naming based on what you actually wrote.",
  },
  {
    n: "03",
    title: "Leave with a Clarity Summary",
    body: "Walk out with a one-page Clarity Summary you can act on from Monday. The thinking is yours. The shape is done.",
  },
];

// ---------------------------------------------------------------------------
const GoldButton = ({ href, to, children, testId }) => {
  const style = {
    background: GOLD,
    color: "#0C447C",
    border: "none",
    padding: "14px 28px",
    borderRadius: "2px",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.04em",
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
    transition: "transform .15s ease, background .15s ease, box-shadow .15s ease",
    textDecoration: "none",
  };
  const hoverIn = (e) => {
    e.currentTarget.style.background = GOLD_HOVER;
    e.currentTarget.style.transform = "translateY(-1px)";
    e.currentTarget.style.boxShadow = "0 8px 24px -10px rgba(201, 168, 76, 0.6)";
  };
  const hoverOut = (e) => {
    e.currentTarget.style.background = GOLD;
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "none";
  };
  if (href) {
    return (
      <a href={href} data-testid={testId} style={style} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
        {children} <ArrowRight size={16} />
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} style={style} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
      {children} <ArrowRight size={16} />
    </Link>
  );
};

const SecondaryLink = ({ to, href, children, testId }) => {
  const baseClass =
    "inline-flex items-center gap-2 text-[14px] text-bos-primary bos-link-underline tracking-[0.01em] hover:opacity-80 transition-opacity";
  if (href) {
    return (
      <a href={href} data-testid={testId} className={baseClass}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={baseClass}>
      {children}
    </Link>
  );
};

// ---------------------------------------------------------------------------
const Hero = () => (
  <section className="border-b border-bos-ice" data-testid="kit-hero">
    <div className="mx-auto max-w-[1100px] px-6 lg:px-10 pt-24 pb-20 lg:pt-32 lg:pb-24">
      <p className="bos-eyebrow">The Clarity Kit · A BuildOnce Studio product</p>
      <h1
        className="mt-6 text-4xl sm:text-5xl lg:text-[64px] font-medium text-bos-navy tracking-tight leading-[1.05] max-w-4xl"
        data-testid="kit-hero-title"
      >
        Get clear on what to <span className="text-bos-primary">build, say, and sell</span> first.
      </h1>
      <p
        className="mt-8 max-w-2xl text-[17px] text-bos-charcoal/85 leading-[1.75]"
        data-testid="kit-hero-subheadline"
      >
        A self-guided decision-making tool for founders, SME owners,
        consultants, coaches, solopreneurs, corporate leavers, and
        fractional leaders who need to decide what to build, say, and
        sell first.
      </p>
      <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
        <GoldButton href={STRIPE_LINK} testId="kit-hero-cta-primary">
          Begin the Kit · SGD 97
        </GoldButton>
        <a
          href={BUNDLE_STRIPE_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="kit-hero-cta-bundle"
          className="text-[14px] text-bos-navy font-medium underline underline-offset-4 decoration-bos-navy/40 hover:decoration-bos-navy transition-colors"
        >
          Or get the Bundle · Kit + Diagnostic · SGD 497 →
        </a>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
        <SecondaryLink to="/clarity-diagnostic" testId="kit-hero-cta-secondary">
          Try the Free Clarity Check
        </SecondaryLink>
      </div>
      <p className="mt-6 text-[12px] text-bos-charcoal/55 tracking-[0.04em] max-w-md leading-[1.55]" data-testid="kit-hero-pricing-sublabel">
        Founding cohort price of SGD 97 applies to the first 50 buyers.
        Standard price is SGD 149.
      </p>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
const WhatsInside = () => (
  <section className="border-b border-bos-ice" data-testid="kit-whats-inside">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20 lg:py-24">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 mb-14">
        <div>
          <p className="bos-eyebrow">What is inside</p>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-[44px] font-medium text-bos-navy tracking-tight leading-[1.12]">
            Six guided <span className="text-bos-primary">exercises.</span>
          </h2>
        </div>
        <p className="text-[16px] text-bos-charcoal/85 leading-[1.85] max-w-2xl pt-3">
          Each exercise gives you one clear decision to make and one concrete artefact
          you can keep. No fluff. No filler. The thinking is yours; the structure is done.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-8" data-testid="kit-sections-grid">
        {SECTIONS.map((s) => (
          <article
            key={s.num}
            data-testid={`kit-section-${s.num}`}
            className="border-l border-bos-ice pl-5 hover:border-bos-mid transition-colors"
          >
            <div className="text-[11px] tracking-[0.18em] uppercase text-bos-mid">
              {s.num}
            </div>
            <h3 className="mt-3 text-[20px] font-medium text-bos-navy tracking-tight leading-[1.2]">
              {s.title}
            </h3>
            <p className="mt-2 text-[14px] text-bos-charcoal/75 leading-[1.7]">
              {s.line}
            </p>
          </article>
        ))}
      </div>

      <div
        className="mt-16 bg-bos-ice/50 border border-bos-light/40 rounded-sm px-6 py-7 lg:px-8 lg:py-8"
        data-testid="kit-bonuses-row"
      >
        <p className="bos-eyebrow mb-5">Plus 3 bonuses included</p>
        <div className="grid sm:grid-cols-3 gap-x-8 gap-y-5">
          {BONUSES.map((b) => (
            <div key={b.title} className="flex items-start gap-3" data-testid={`kit-bonus-${b.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <Check size={16} className="mt-1 text-bos-primary shrink-0" />
              <div>
                <div className="text-[14px] font-medium text-bos-navy leading-[1.35]">
                  {b.title}
                </div>
                <div className="text-[13px] text-bos-charcoal/70 leading-[1.6] mt-1">
                  {b.body}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
const HowItWorks = () => (
  <section className="border-b border-bos-ice" data-testid="kit-how-it-works">
    <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-20 lg:py-24">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-10 lg:gap-16 mb-14">
        <div>
          <p className="bos-eyebrow">How it works</p>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-[44px] font-medium text-bos-navy tracking-tight leading-[1.12]">
            Three quiet <span className="text-bos-primary">steps.</span>
          </h2>
        </div>
        <p className="text-[16px] text-bos-charcoal/85 leading-[1.85] max-w-2xl pt-3">
          You write the answers. The Kit gives the shape. The AI gives the second pair
          of eyes. You leave with something you can use.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-x-8 gap-y-10 lg:gap-x-12" data-testid="kit-steps-grid">
        {STEPS.map((s, i) => (
          <div key={s.n} data-testid={`kit-step-${s.n}`} className="relative">
            <div className="text-[64px] lg:text-[72px] font-medium text-bos-light leading-none tracking-tight select-none">
              {s.n}
            </div>
            <h3 className="mt-4 text-[20px] font-medium text-bos-navy tracking-tight leading-[1.25]">
              {s.title}
            </h3>
            <p className="mt-3 text-[15px] text-bos-charcoal/85 leading-[1.75] max-w-sm">
              {s.body}
            </p>
            {i < STEPS.length - 1 && (
              <div className="hidden md:block absolute top-[34px] -right-6 lg:-right-8 text-bos-mid/40">
                <ArrowRight size={20} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
const Pricing = () => (
  <section className="bg-bos-ice/40" data-testid="kit-pricing">
    <div className="mx-auto max-w-[960px] px-6 lg:px-10 py-20 lg:py-28">
      <div className="text-center">
        <p className="bos-eyebrow">Pricing</p>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-[44px] font-medium text-bos-navy tracking-tight leading-[1.1]">
          One purchase. Yours <span className="text-bos-primary">to keep.</span>
        </h2>
        <p className="mt-5 text-[13px] text-bos-charcoal/60 max-w-md mx-auto leading-[1.6]">
          Founding cohort price of SGD 97 applies to the first 50 buyers.
          Standard price is SGD 149.
        </p>
      </div>

      {/* Two checkout options, equal visual weight per Change 5. */}
      <div className="mt-12 grid md:grid-cols-2 gap-5" data-testid="kit-pricing-options">
        {/* Option 1: Kit only */}
        <div className="bg-bos-white border border-bos-ice rounded-sm p-7 lg:p-8 flex flex-col" data-testid="kit-option-kit">
          <div className="text-[11px] tracking-[0.16em] uppercase text-bos-mid">Option 01</div>
          <h3 className="mt-3 text-[20px] font-medium text-bos-navy">
            Clarity Kit · SGD 97 founding cohort price
          </h3>
          <p className="mt-3 text-[13.5px] text-bos-charcoal/75 leading-[1.65] flex-1">
            Founding cohort price. Closes when the cohort is full.
          </p>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-[44px] lg:text-[52px] font-medium text-bos-navy leading-none tracking-tight" data-testid="kit-pricing-founding">
              SGD 97
            </span>
            <span className="text-[13px] text-bos-charcoal/55">
              standard <span className="line-through" data-testid="kit-pricing-standard">SGD 149</span>
            </span>
          </div>
          <div className="mt-7">
            <GoldButton href={STRIPE_LINK} testId="kit-pricing-cta-primary">
              Begin the Kit · SGD 97
            </GoldButton>
          </div>
        </div>

        {/* Option 2: Kit + Diagnostic bundle */}
        <div className="bg-bos-white border border-bos-ice rounded-sm p-7 lg:p-8 flex flex-col" data-testid="kit-option-bundle">
          <div className="text-[11px] tracking-[0.16em] uppercase text-bos-primary">Option 02 · Bundle</div>
          <h3 className="mt-3 text-[20px] font-medium text-bos-navy">
            Clarity Kit + Diagnostic Bundle · SGD 497
          </h3>
          <p className="mt-3 text-[13.5px] text-bos-charcoal/75 leading-[1.65] flex-1">
            Includes the full Kit and a 75-minute 1:1 Diagnostic session with
            Jophy. Saves SGD 50 vs buying separately. No founding cohort
            required. Available at any time.
          </p>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-[44px] lg:text-[52px] font-medium text-bos-navy leading-none tracking-tight" data-testid="bundle-pricing">
              SGD 497
            </span>
            <span className="text-[13px] text-bos-charcoal/55">
              save SGD 50
            </span>
          </div>
          <div className="mt-7">
            <GoldButton href={BUNDLE_STRIPE_LINK} testId="bundle-pricing-cta-primary">
              Get the Bundle · SGD 497
            </GoldButton>
          </div>
        </div>
      </div>

      <div className="mt-10 text-center">
        <SecondaryLink to="/clarity-ai-demo" testId="kit-pricing-cta-secondary">
          Not sure yet? Try the Free Clarity Check.
        </SecondaryLink>
      </div>

      <p className="mt-10 text-[12px] text-bos-charcoal/55 max-w-md mx-auto leading-[1.7] text-center">
        Already a buyer? <Link to="/clarity-kit-app" className="text-bos-primary bos-link-underline" data-testid="kit-pricing-existing-buyer">Begin the Kit →</Link>
      </p>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
export default function ClarityKit() {
  useSeo({
    title: "The Clarity Kit · BuildOnce Studio",
    description:
      "A self-guided decision tool for professionals with too many directions and no clear starting point. Six guided exercises. SGD 97 founding price.",
  });
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    trackPageView("/clarity-kit");
  }, []);

  return (
    <div data-testid="clarity-kit-info-page">
      <Hero />
      <WhatsInside />
      <HowItWorks />
      <Pricing />
    </div>
  );
}
